import React, { SyntheticEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { stripeApi, Currency, Plan } from "../../api/stripe";
import "../../components/LangSwitcher/LangSwitcher.css";
import marginLogo from "../../assets/vectorlogo.png";
import { buildWhatsappUrl } from "../../pages/Landing/utm";
import "./CheckoutPanel.css";

const MANAGER_PHONE = "77011087185";

interface CurrencyMeta {
  code: Currency;
  full: number;
  format: (n: number) => string;
}

const CURRENCIES: CurrencyMeta[] = [
  { code: "USD", full: 95,    format: (n) => `$${n.toLocaleString("en-US")}` },
  { code: "EUR", full: 80,    format: (n) => `€${n.toLocaleString("en-US")}` },
  { code: "KZT", full: 40000, format: (n) => `${n.toLocaleString("ru-RU")} ₸` },
  { code: "KGS", full: 7000,  format: (n) => `${n.toLocaleString("ru-RU")} сом` },
  { code: "AZN", full: 160,   format: (n) => `${n.toLocaleString("en-US")} ₼` },
  { code: "BYN", full: 240,   format: (n) => `Br ${n.toLocaleString("en-US")}` },
  { code: "GEL", full: 250,   format: (n) => `${n.toLocaleString("en-US")} ₾` },
  { code: "TRY", full: 4000,  format: (n) => `${n.toLocaleString("en-US")} ₺` },
];

const PLANS: { id: Plan; fraction: number; labelKey: "planFull" | "planHalf" | "planQuarter" }[] = [
  { id: "full",    fraction: 1.0,  labelKey: "planFull" },
  { id: "half",    fraction: 0.5,  labelKey: "planHalf" },
  { id: "quarter", fraction: 0.25, labelKey: "planQuarter" },
];

interface DropdownOption<T extends string> {
  value: T;
  label: string;
}

interface DropdownProps<T extends string> {
  id: string;
  label: string;
  options: DropdownOption<T>[];
  value: T;
  onChange: (v: T) => void;
}

function Dropdown<T extends string>({ id, label, options, value, onChange }: DropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const current = options.find((o) => o.value === value);

  return (
    <div className="mc__field" ref={rootRef}>
      <label className="mc__label" htmlFor={id}>{label}</label>
      <div className="mc__dropdown">
        <button
          id={id}
          type="button"
          className="mc__dropdown-trigger"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span>{current?.label}</span>
          <svg width="10" height="6" viewBox="0 0 10 6" aria-hidden>
            <path fill="currentColor" d="M0 0l5 6 5-6z" />
          </svg>
        </button>
        {open && (
          <ul className="mg-lang__menu mc__dropdown-menu" role="listbox">
            {options.map((o) => {
              const active = o.value === value;
              return (
                <li key={o.value} role="option" aria-selected={active}>
                  <button
                    type="button"
                    className={`mg-lang__item ${active ? "mg-lang__item--active" : ""}`}
                    onClick={() => {
                      onChange(o.value);
                      setOpen(false);
                    }}
                  >
                    {o.label}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

const StripeMark: React.FC = () => (
  <svg
    className="mc__stripe"
    viewBox="0 0 60 25"
    aria-label="Stripe"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 9.0c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.56h4.13v14.44h-4.13V5.56zm0-4.7L32.37 0v3.36l-4.13.88V.86zm-4.32 9.35v9.79H19.8V5.56h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9h-3.13v5.93zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.58-.24 1.58-1C6.31 13.85 0 14.84 0 10.2 0 7.34 2.18 5.59 5.52 5.59c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.4.25-1.4.9 0 1.85 6.35.97 6.35 5.56z"
      fillRule="evenodd"
    />
  </svg>
);

const WhatsAppGlyph: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 32 32" aria-hidden>
    <path
      fill="currentColor"
      d="M16 .4C7.4.4.4 7.4.4 16c0 2.8.74 5.43 2.03 7.7L.4 31.6l8.05-2c2.2 1.18 4.7 1.86 7.36 1.86h.01c8.6 0 15.59-7 15.59-15.6 0-4.16-1.62-8.07-4.56-11.02A15.5 15.5 0 0 0 16 .4zm-.01 28.4h-.01a13 13 0 0 1-6.6-1.8l-.47-.28-4.78 1.18 1.27-4.66-.31-.49a12.95 12.95 0 0 1-1.96-6.85c0-7.16 5.83-12.99 13-12.99 3.47 0 6.73 1.35 9.18 3.81a12.92 12.92 0 0 1 3.8 9.19c0 7.15-5.84 12.98-12.99 12.98zm7.12-9.72c-.39-.2-2.31-1.14-2.67-1.27-.36-.13-.62-.2-.88.2-.26.39-1.01 1.27-1.24 1.53-.23.26-.46.29-.85.1-.39-.2-1.65-.61-3.14-1.94a11.85 11.85 0 0 1-2.18-2.71c-.23-.39-.02-.6.17-.79.18-.18.39-.46.59-.69.2-.23.26-.4.39-.66.13-.26.07-.49-.03-.69-.1-.2-.88-2.12-1.21-2.91-.32-.76-.64-.66-.88-.67l-.75-.01c-.26 0-.69.1-1.05.49s-1.37 1.34-1.37 3.27c0 1.92 1.4 3.78 1.6 4.04.2.26 2.76 4.21 6.69 5.91.93.4 1.66.64 2.23.82.94.3 1.79.26 2.46.16.75-.11 2.31-.94 2.64-1.85.33-.91.33-1.69.23-1.85-.1-.16-.36-.26-.75-.46z"
    />
  </svg>
);

export interface CheckoutPanelProps {
  /** Initial currency selection. Defaults to USD. */
  defaultCurrency?: Currency;
}

export const CheckoutPanel: React.FC<CheckoutPanelProps> = ({ defaultCurrency = "USD" }) => {
  const { t } = useTranslation("checkout");
  const [currencyCode, setCurrencyCode] = useState<Currency>(defaultCurrency);
  const [plan, setPlan] = useState<Plan>("full");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Промокод вводится опционально. Валидация только в момент нажатия
  // «Оплатить» — если backend в /stripe/checkout отвергнет код, фронт
  // покажет сообщение «Промокод неверный». Скидка локально не рисуется —
  // итог пользователь увидит на странице Stripe.
  const [promoCode, setPromoCode] = useState("");

  const currency = CURRENCIES.find((c) => c.code === currencyCode)!;
  const fraction = PLANS.find((p) => p.id === plan)!.fraction;
  const planAmount = Math.round(currency.full * fraction);

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await stripeApi.checkout({
        email,
        plan,
        currency: currencyCode,
        promoCode: promoCode.trim() || undefined,
      });
      window.location.href = data.url;
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setError(t("errorEmailExists"));
      } else if (err?.response?.data?.error === "Invalid or expired promo code") {
        setError(t("promoInvalid"));
      } else {
        setError(err?.response?.data?.error || t("errorGeneric"));
      }
      setLoading(false);
    }
  };

  return (
    <div className="mc__panel">
      <div className="mc__brand">
        <img src={marginLogo} alt="lovestoria" />
      </div>

      <div className="mc__heading">
        <h1 className="mc__title">{t("title")}</h1>
        <div className="mc__amount">{currency.format(planAmount)}</div>
      </div>

      <form className="mc__form" onSubmit={handleSubmit}>
        <Dropdown<Currency>
          id="currency"
          label={t("currencyLabel")}
          value={currencyCode}
          onChange={setCurrencyCode}
          options={CURRENCIES.map((c) => ({
            value: c.code,
            label: `${c.code} — ${c.format(c.full)}`,
          }))}
        />

        <Dropdown<Plan>
          id="plan"
          label={t("planLabel")}
          value={plan}
          onChange={setPlan}
          options={PLANS.map((p) => ({
            value: p.id,
            label: `${t(p.labelKey)} — ${currency.format(Math.round(currency.full * p.fraction))}`,
          }))}
        />

        <div className="mc__field">
          <label className="mc__label" htmlFor="email">{t("emailLabel")}</label>
          <input
            id="email"
            type="email"
            className="mc__input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("emailPlaceholder")}
            required
            autoComplete="email"
          />
        </div>

        <div className="mc__field">
          <label className="mc__label" htmlFor="promo">{t("promoLabel")}</label>
          <input
            id="promo"
            type="text"
            className="mc__input mc__promo-input"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            placeholder={t("promoPlaceholder")}
            autoComplete="off"
          />
        </div>

        {error && <div className="mc__error">{error}</div>}

        <button type="submit" className="mc__submit" disabled={loading || !email}>
          <span>{loading ? t("submitting") : t("submit")}</span>
          <StripeMark />
        </button>
      </form>

      {plan !== "full" && (
        <div className="mc__partial">
          <div className="mc__partial-intro">{t("partialIntro")}</div>
          <p className="mc__partial-line">
            {t("partialDeposit", {
              amount: currency.format(planAmount),
              percent: Math.round(fraction * 100),
            })}
          </p>
          <p className="mc__partial-line">
            {t("partialRemainder", {
              amount: currency.format(currency.full - planAmount),
            })}
          </p>
        </div>
      )}

      <div className="mc__support">
        <div className="mc__support-q">{t("supportQuestion")}</div>
        <a
          className="mc__support-cta"
          href={buildWhatsappUrl(MANAGER_PHONE)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <WhatsAppGlyph />
          <span>{t("supportCta")}</span>
        </a>
        <div className="mc__support-meta">{t("supportMeta")}</div>
      </div>
    </div>
  );
};
